import React from 'react';
import { BrowserRouter as Router, Routes, Route, Outlet } from 'react-router-dom';
import { BranchProvider } from './context/BranchContext';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import PrivateRoute from './components/PrivateRoute';
import ContactRequests from './pages/ContactRequests';
import ContactRequestDetail from './pages/ContactRequestDetail';
import Scheduler from './pages/Scheduler';
import Patients from './pages/Patients';
import AppLayout from './components/AppLayout';
import RoleBasedRoute from './components/RoleBasedRoute';
import Admin from './pages/Admin';
import Settings from './pages/Settings';
import SurgeryScheduler from './pages/SurgeryScheduler';
import SurgicalProtocol from './pages/SurgicalProtocol';
import SurgerySupplies from './pages/SurgerySupplies';
import SurgeryReferral from './pages/SurgeryReferral';
import SurgeryWorklist from './pages/SurgeryWorklist';
import Hospital from './pages/Hospital';
import HospitalFlowsheet from './pages/HospitalFlowsheet';
import HospitalEpicrisis from './pages/HospitalEpicrisis';
import HospitalAdmission from './pages/HospitalAdmission';
import ClinicalCaseSearch from './pages/ClinicalCaseSearch';
import PublicUDLARequest from './pages/PublicUDLARequest';
import PublicBooking from './pages/PublicBooking';
import PublicResults from './pages/PublicResults';
import PaymentResult from './pages/PaymentResult';
import BookingSuccess from './pages/BookingSuccess';
import PaymentError from './pages/PaymentError';
import PublicBookingConfirmation from './pages/PublicBookingConfirmation';
import PharmacyMain from './pages/PharmacyMain';
import DebugAuth from './pages/DebugAuth';
import Codificacion from './pages/Codificacion';
import AgendaConfig from './pages/AgendaConfig';
import ArancelConfig from './pages/ArancelConfig';
import LabDashboard from './pages/LabDashboard';
import LabOrders from './pages/LabOrders';
import NewLabOrder from './pages/NewLabOrder';
import UploadResult from './pages/UploadResult';
import ReviewResult from './pages/ReviewResult';
import ExamCatalog from './pages/ExamCatalog';
import SuppliesCatalog from './pages/SuppliesCatalog';
import PatientLabHistory from './pages/PatientLabHistory';
import PatientImagingHistory from './pages/PatientImagingHistory';
import ImagingReportsHistory from './pages/ImagingReportsHistory';
import LabOrderDetail from './pages/LabOrderDetail';
import BulkLabImport from './pages/BulkLabImport';
import PatientRecord from './pages/PatientRecord';
import ImagingDashboard from './pages/ImagingDashboard';
import ImagingOrders from './pages/ImagingOrders';
import NewImagingOrder from './pages/NewImagingOrder';
import ImagingOrderDetail from './pages/ImagingOrderDetail';
import ImagingReport from './pages/ImagingReport';
import ImagingExamCatalog from './pages/ImagingExamCatalog';
import ClinicalConsultation from './pages/ClinicalConsultation';
import GenerateReport from './pages/GenerateReport';
import CajaDashboard from './pages/caja/CajaDashboard';
import TicketVenta from './pages/caja/TicketVenta';
import LibroVentas from './pages/caja/LibroVentas';
import PresupuestoList from './pages/caja/PresupuestoList';
import PresupuestoBuilder from './pages/caja/PresupuestoBuilder';
import CuentaPaciente from './pages/caja/CuentaPaciente';
import InformeFinanciero from './pages/caja/InformeFinanciero';
import ListaPrecios from './pages/caja/ListaPrecios';
import Devoluciones from './pages/caja/Devoluciones';
import Tutores from './pages/Tutores';
import AgendaDashboardPage from './pages/AgendaDashboardPage';
import SurgeryDashboardPage from './pages/SurgeryDashboardPage';
import HospitalDashboardPage from './pages/HospitalDashboardPage';
import GoalsConfig from './pages/GoalsConfig';
import Convenios from './pages/Convenios';
import HonorariosMedicos from './pages/HonorariosMedicos';
import NotificationsConfig from './pages/NotificationsConfig';
import UserProfile from './pages/UserProfile';

// Componente que envuelve las rutas protegidas con el layout
const ProtectedLayout = () => (
  <PrivateRoute>
    <AppLayout>
      <Outlet /> {/* Aquí se renderizará el componente de la ruta hija */}
    </AppLayout>
  </PrivateRoute>
);

function App() {
  return (
    <BranchProvider>
    <Router>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/solicitud-udla" element={<PublicUDLARequest />} />
        <Route path="/agendar" element={<PublicBooking />} />
        <Route path="/resultados" element={<PublicResults />} />
        <Route path="/booking/payment-result" element={<PaymentResult />} />
        <Route path="/booking/success" element={<BookingSuccess />} />
        <Route path="/booking/payment-error" element={<PaymentError />} />
        <Route path="/confirmar/:code" element={<PublicBookingConfirmation />} />
        <Route element={<ProtectedLayout />}>
          <Route 
            path="/" 
            element={
              <RoleBasedRoute allowedRoles={['admin', 'gestion', 'Gestión Clínica', 'Director Médico', 'Gerente Sucursal']}>
                <Dashboard />
              </RoleBasedRoute>
            } 
          />
          <Route 
            path="/contact-requests" 
            element={
              <RoleBasedRoute allowedRoles={['admin', 'gestion', 'Director Médico', 'derivador', 'Gerente Sucursal']}>
                <ContactRequests />
              </RoleBasedRoute>
            } 
          />
          <Route 
            path="/contact-requests/:id" 
            element={
              <RoleBasedRoute allowedRoles={['admin', 'gestion', 'Director Médico']}>
                <ContactRequestDetail />
              </RoleBasedRoute>
            } 
          />
          <Route 
            path="/scheduler" 
            element={
              <RoleBasedRoute allowedRoles={['admin', 'Médico Veterinario', 'gestion', 'Director Médico', 'Gerente Sucursal']}>
                <Scheduler />
              </RoleBasedRoute>
            } 
          />
          <Route 
            path="/agenda/dashboard" 
            element={
              <RoleBasedRoute allowedRoles={['admin', 'gestion', 'Director Médico', 'Gerente Sucursal']}>
                <AgendaDashboardPage />
              </RoleBasedRoute>
            } 
          />
          <Route 
            path="/surgery-scheduler" 
            element={
              <RoleBasedRoute allowedRoles={['admin', 'Médico Veterinario', 'gestion', 'Director Médico', 'Gerente Sucursal']}>
                <SurgeryScheduler />
              </RoleBasedRoute>
            } 
          />
          <Route
            path="/hospital"
            element={
              <RoleBasedRoute allowedRoles={['admin', 'Médico Veterinario', 'gestion', 'Director Médico', 'Gerente Sucursal']}>
                <Hospital />
              </RoleBasedRoute>
            }
          />
          <Route
            path="/hospital-flowsheet/:hospitalizationId"
            element={
              <RoleBasedRoute allowedRoles={['admin', 'Médico Veterinario', 'gestion', 'Director Médico', 'Gerente Sucursal']}>
                <HospitalFlowsheet />
              </RoleBasedRoute>
            }
          />
          <Route
            path="/clinical-cases"
            element={
              <RoleBasedRoute allowedRoles={['admin', 'Médico Veterinario', 'gestion', 'Gestión Clínica', 'Director Médico', 'Gerente Sucursal']}>
                <ClinicalCaseSearch />
              </RoleBasedRoute>
            }
          />
          <Route
            path="/hospital-epicrisis/:hospitalizationId"
            element={
              <RoleBasedRoute allowedRoles={['admin', 'Médico Veterinario', 'gestion', 'Director Médico', 'Gerente Sucursal']}>
                <HospitalEpicrisis />
              </RoleBasedRoute>
            }
          />
          <Route
            path="/hospital/admission"
            element={
              <RoleBasedRoute allowedRoles={['admin', 'gestion', 'Director Médico', 'Gerente Sucursal']}>
                <HospitalAdmission />
              </RoleBasedRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <RoleBasedRoute allowedRoles={['admin', 'gestion', 'Director Médico', 'Gerente Sucursal', 'Médico Veterinario', 'Tecnólogo Médico', 'Radiologo', 'derivador', 'Supervisor de Caja', 'user']}>
                <UserProfile />
              </RoleBasedRoute>
            }
          />
          <Route
            path="/surgery-supplies/:surgeryId"
            element={
              <RoleBasedRoute allowedRoles={['admin', 'Médico Veterinario', 'gestion', 'Director Médico', 'Gerente Sucursal']}>
                <SurgerySupplies />
              </RoleBasedRoute>
            }
          />
          <Route
            path="/surgery-protocol/:surgeryId"
            element={
              <RoleBasedRoute allowedRoles={['admin', 'Médico Veterinario', 'gestion', 'Director Médico', 'Gerente Sucursal']}>
                <SurgicalProtocol />
              </RoleBasedRoute>
            }
          />
          <Route
            path="/surgery/referral"
            element={
              <RoleBasedRoute allowedRoles={['admin', 'Médico Veterinario', 'gestion', 'Director Médico', 'Gerente Sucursal']}>
                <SurgeryReferral />
              </RoleBasedRoute>
            }
          />
          <Route
            path="/surgery/worklist"
            element={
              <RoleBasedRoute allowedRoles={['admin', 'gestion', 'Director Médico', 'Gerente Sucursal']}>
                <SurgeryWorklist />
              </RoleBasedRoute>
            }
          />
          <Route 
            path="/patients" 
            element={
              <RoleBasedRoute allowedRoles={['admin', 'Médico Veterinario', 'gestion', 'Gestión Clínica', 'Tecnólogo Médico', 'Director Médico', 'Radiologo', 'Gerente Sucursal']}>
                <Patients />
              </RoleBasedRoute>
            } 
          />
          <Route
            path="/patients/:patientId/record"
            element={
              <RoleBasedRoute allowedRoles={['admin', 'Médico Veterinario', 'gestion', 'Gestión Clínica', 'Tecnólogo Médico', 'Director Médico', 'Radiologo', 'Gerente Sucursal']}>
                <PatientRecord />
              </RoleBasedRoute>
            }
          />
          <Route
            path="/patients/:patientId/consultation"
            element={
              <RoleBasedRoute allowedRoles={['admin', 'Médico Veterinario', 'gestion', 'Tecnólogo Médico', 'Director Médico', 'Radiologo', 'Gerente Sucursal']}>
                <ClinicalConsultation />
              </RoleBasedRoute>
            }
          />
          <Route
            path="/patients/:patientId/consultation/:consultationId"
            element={
              <RoleBasedRoute allowedRoles={['admin', 'Médico Veterinario', 'gestion', 'Tecnólogo Médico', 'Director Médico', 'Radiologo', 'Gerente Sucursal']}>
                <ClinicalConsultation />
              </RoleBasedRoute>
            }
          />
          <Route 
            path="/admin" 
            element={
              <RoleBasedRoute allowedRoles={['admin']}>
                <Admin />
              </RoleBasedRoute>
            }
          />
          <Route
            path="/agenda-config"
            element={
              <RoleBasedRoute allowedRoles={['admin']}>
                <AgendaConfig />
              </RoleBasedRoute>
            }
          />
          <Route
            path="/arancel-config"
            element={
              <RoleBasedRoute allowedRoles={['admin', 'Gerente Sucursal']}>
                <ArancelConfig />
              </RoleBasedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <RoleBasedRoute allowedRoles={['admin', 'Gerente Sucursal']}>
                <Settings />
              </RoleBasedRoute>
            }
          />
          <Route 
            path="/pharmacy" 
            element={
              <RoleBasedRoute allowedRoles={['admin', 'Gerente Sucursal']}>
                <PharmacyMain />
              </RoleBasedRoute>
            }
          />
          <Route 
            path="/codificacion" 
            element={
              <RoleBasedRoute allowedRoles={['admin']}>
                <Codificacion />
              </RoleBasedRoute>
            }
          />
          <Route 
            path="/debug-auth" 
            element={
              <RoleBasedRoute allowedRoles={['admin']}>
                <DebugAuth />
              </RoleBasedRoute>
            }
          />

          {/* ── Módulo Caja / Finanzas ──────────────────────────────── */}
          <Route path="/caja" element={<RoleBasedRoute allowedRoles={['admin', 'gestion', 'Gerente Sucursal', 'Supervisor de Caja']}><CajaDashboard /></RoleBasedRoute>} />
          <Route path="/caja/ticket" element={<RoleBasedRoute allowedRoles={['admin', 'gestion', 'Gerente Sucursal', 'Supervisor de Caja']}><TicketVenta /></RoleBasedRoute>} />
          <Route path="/caja/libro-ventas" element={<RoleBasedRoute allowedRoles={['admin', 'gestion', 'Gerente Sucursal', 'Supervisor de Caja']}><LibroVentas /></RoleBasedRoute>} />
          <Route path="/caja/devoluciones" element={<RoleBasedRoute allowedRoles={['admin', 'gestion', 'Gerente Sucursal', 'Supervisor de Caja']}><Devoluciones /></RoleBasedRoute>} />
          <Route path="/caja/lista-precios" element={<RoleBasedRoute allowedRoles={['admin', 'gestion', 'Director Médico', 'Gerente Sucursal']}><ListaPrecios /></RoleBasedRoute>} />
          <Route path="/presupuestos" element={<RoleBasedRoute allowedRoles={['admin', 'gestion', 'Director Médico', 'Gerente Sucursal']}><PresupuestoList /></RoleBasedRoute>} />
          <Route path="/presupuestos/nuevo" element={<RoleBasedRoute allowedRoles={['admin', 'gestion', 'Médico Veterinario', 'Director Médico', 'Gerente Sucursal']}><PresupuestoBuilder /></RoleBasedRoute>} />
          <Route path="/presupuestos/:id" element={<RoleBasedRoute allowedRoles={['admin', 'gestion', 'Médico Veterinario', 'Director Médico', 'Gerente Sucursal']}><PresupuestoBuilder /></RoleBasedRoute>} />
          <Route path="/cuenta/:ownerId" element={<RoleBasedRoute allowedRoles={['admin', 'gestion', 'Director Médico', 'Gerente Sucursal']}><CuentaPaciente /></RoleBasedRoute>} />
          <Route path="/reportes/financiero" element={<RoleBasedRoute allowedRoles={['admin', 'gestion', 'Director Médico', 'Gerente Sucursal', 'Supervisor de Caja']}><InformeFinanciero /></RoleBasedRoute>} />

          {/* ── Módulo Laboratorio Clínico ─────────────────────────── */}
          <Route path="/lab" element={<RoleBasedRoute allowedRoles={['admin', 'gestion', 'Tecnólogo Médico', 'Director Médico', 'Gerente Sucursal']}><LabDashboard /></RoleBasedRoute>} />
          <Route path="/lab/orders" element={<RoleBasedRoute allowedRoles={['admin', 'gestion', 'Tecnólogo Médico', 'Director Médico', 'Gerente Sucursal']}><LabOrders /></RoleBasedRoute>} />
          <Route path="/lab/orders/new" element={<RoleBasedRoute allowedRoles={['admin', 'gestion', 'Tecnólogo Médico', 'Director Médico', 'Médico Veterinario', 'Gerente Sucursal']}><NewLabOrder /></RoleBasedRoute>} />
          <Route path="/lab/orders/:id" element={<RoleBasedRoute allowedRoles={['admin', 'gestion', 'Tecnólogo Médico', 'Director Médico', 'Médico Veterinario', 'Gerente Sucursal']}><LabOrderDetail /></RoleBasedRoute>} />
          <Route path="/lab/upload" element={<RoleBasedRoute allowedRoles={['admin', 'Tecnólogo Médico']}><UploadResult /></RoleBasedRoute>} />
          <Route path="/lab/review/:resultId" element={<RoleBasedRoute allowedRoles={['admin', 'gestion', 'Tecnólogo Médico', 'Director Médico', 'Médico Veterinario', 'Gerente Sucursal']}><ReviewResult /></RoleBasedRoute>} />
          <Route path="/lab/report/:orderId" element={<RoleBasedRoute allowedRoles={['admin', 'gestion', 'Tecnólogo Médico', 'Director Médico', 'Médico Veterinario', 'Gerente Sucursal']}><GenerateReport /></RoleBasedRoute>} />
          <Route path="/lab/catalog" element={<RoleBasedRoute allowedRoles={['admin', 'Tecnólogo Médico', 'Director Médico', 'Gerente Sucursal']}><ExamCatalog /></RoleBasedRoute>} />
          <Route path="/lab/supplies" element={<RoleBasedRoute allowedRoles={['admin', 'Tecnólogo Médico', 'Director Médico', 'Gerente Sucursal']}><SuppliesCatalog /></RoleBasedRoute>} />
          <Route path="/lab/history" element={<RoleBasedRoute allowedRoles={['admin', 'gestion', 'Tecnólogo Médico', 'Director Médico', 'Médico Veterinario', 'Gerente Sucursal']}><PatientLabHistory /></RoleBasedRoute>} />
          <Route path="/lab/import" element={<RoleBasedRoute allowedRoles={['admin', 'Tecnólogo Médico', 'Director Médico']}><BulkLabImport /></RoleBasedRoute>} />

          {/* ── Módulo Diagnóstico por Imágenes ─────────────────────── */}
          <Route path="/imaging" element={<RoleBasedRoute allowedRoles={['admin', 'Médico Veterinario', 'gestion', 'Director Médico', 'Radiologo', 'Gerente Sucursal']}><ImagingDashboard /></RoleBasedRoute>} />
          <Route path="/imaging/orders" element={<RoleBasedRoute allowedRoles={['admin', 'Médico Veterinario', 'gestion', 'Director Médico', 'Radiologo', 'Gerente Sucursal']}><ImagingOrders /></RoleBasedRoute>} />
          <Route path="/imaging/new" element={<RoleBasedRoute allowedRoles={['admin', 'Médico Veterinario', 'Director Médico', 'Radiologo']}><NewImagingOrder /></RoleBasedRoute>} />
          <Route path="/imaging/orders/:id" element={<RoleBasedRoute allowedRoles={['admin', 'Médico Veterinario', 'gestion', 'Director Médico', 'Radiologo', 'Gerente Sucursal']}><ImagingOrderDetail /></RoleBasedRoute>} />
          <Route path="/imaging/history" element={<RoleBasedRoute allowedRoles={['admin', 'Médico Veterinario', 'Director Médico', 'Radiologo']}><PatientImagingHistory /></RoleBasedRoute>} />
          <Route path="/imaging/report/:id" element={<RoleBasedRoute allowedRoles={['admin', 'Director Médico', 'Radiologo']}><ImagingReport /></RoleBasedRoute>} />
          <Route path="/imaging/reports-history" element={<RoleBasedRoute allowedRoles={['admin', 'Médico Veterinario', 'Director Médico', 'Radiologo', 'Gerente Sucursal']}><ImagingReportsHistory /></RoleBasedRoute>} />
          <Route path="/imaging/catalog" element={<RoleBasedRoute allowedRoles={['admin', 'Director Médico', 'Radiologo', 'Gerente Sucursal']}><ImagingExamCatalog /></RoleBasedRoute>} />

          {/* ── Nuevas rutas del refactor de menú ───────────────────── */}
          <Route path="/tutors" element={<RoleBasedRoute allowedRoles={['admin', 'gestion', 'Director Médico', 'Gerente Sucursal']}><Tutores /></RoleBasedRoute>} />
          <Route path="/surgery/dashboard" element={<RoleBasedRoute allowedRoles={['admin', 'gestion', 'Director Médico', 'Gerente Sucursal']}><SurgeryDashboardPage /></RoleBasedRoute>} />
          <Route path="/hospital/dashboard" element={<RoleBasedRoute allowedRoles={['admin', 'gestion', 'Director Médico', 'Gerente Sucursal']}><HospitalDashboardPage /></RoleBasedRoute>} />
          <Route path="/scheduler/create" element={<RoleBasedRoute allowedRoles={['admin']}><AgendaConfig /></RoleBasedRoute>} />
          <Route path="/goals" element={<RoleBasedRoute allowedRoles={['admin', 'Gerente Sucursal']}><GoalsConfig /></RoleBasedRoute>} />
          <Route path="/convenios" element={<RoleBasedRoute allowedRoles={['admin', 'Gerente Sucursal']}><Convenios /></RoleBasedRoute>} />
          <Route path="/admin/honorarios-medicos" element={<RoleBasedRoute allowedRoles={['admin', 'Gerente Sucursal']}><HonorariosMedicos /></RoleBasedRoute>} />
          <Route path="/admin/notificaciones" element={<RoleBasedRoute allowedRoles={['admin', 'Gerente Sucursal']}><NotificationsConfig /></RoleBasedRoute>} />
        </Route>
      </Routes>
    </Router>
    </BranchProvider>
  );
}

export default App;
